import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft, Plus, Pencil, Trash2, Users, Wallet, Calculator, Play, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { todayLocalIso } from "@/lib/dateUtils";

const fmtKES = (v) => `KES ${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const todayIso = () => todayLocalIso();
const monthIso = () => todayIso().slice(0, 7);

const blank = () => ({
  id: null, name: "", phone: "", role: "House helper", employment_type: "permanent",
  base_salary_kes: 0, is_payroll_eligible: true, active: true,
  start_date: todayIso(), end_date: "",
  kra_pin: "", nssf_number: "", sha_number: "",
  bank_name: "", bank_account: "", next_of_kin: "", next_of_kin_phone: "",
  notes: "",
});

export default function Staff() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [breakdown, setBreakdown] = useState(null);
  const [period, setPeriod] = useState(monthIso());
  const [running, setRunning] = useState(false);

  const isAdmin = ["admin", "global_admin"].includes(user?.role);
  const canWrite = ["admin", "global_admin", "hospitality_coordinator", "booking_coordinator"].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/housing/cleaners");
      setStaff(data);
    } catch (e) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const payrollEligible = useMemo(() => staff.filter((s) => s.is_payroll_eligible && s.active), [staff]);
  const totalGross = useMemo(() => payrollEligible.reduce((sum, s) => sum + (Number(s.base_salary_kes) || 0), 0), [payrollEligible]);

  const openNew = () => setDraft(blank());
  const openEdit = (s) => setDraft({ ...blank(), ...s, end_date: s.end_date || "" });

  const save = async () => {
    if (!draft.name?.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    const body = { ...draft, base_salary_kes: Number(draft.base_salary_kes || 0), end_date: draft.end_date || null };
    delete body.id;
    try {
      if (draft.id) {
        await api.put(`/housing/cleaners/${draft.id}`, body);
        toast.success("Staff updated");
      } else {
        await api.post("/housing/cleaners", body);
        toast.success("Staff added");
      }
      setDraft(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const del = async (s) => {
    if (!window.confirm(`Deactivate ${s.name}?`)) return;
    try { await api.delete(`/housing/cleaners/${s.id}`); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const previewBreakdown = async (gross) => {
    try {
      const { data } = await api.get(`/housing/payroll/calculator?gross_kes=${gross}`);
      setBreakdown(data);
    } catch (e) { toast.error("Calc failed"); }
  };

  const runPayroll = async () => {
    if (!window.confirm(`Run payroll for ${period}? This generates expense entries for ${payrollEligible.length} eligible staff.`)) return;
    setRunning(true);
    try {
      const { data } = await api.post("/housing/payroll/run", { period });
      toast.success(`${data.created} payroll runs created, ${data.skipped} already existed`);
    } catch (e) { toast.error(e.response?.data?.detail || "Payroll failed"); }
    finally { setRunning(false); }
  };

  if (loading) return <div className="p-6 text-[#7A8A84]">Loading staff…</div>;

  return (
    <div className="space-y-6 fade-in" data-testid="staff-page">
      <Button variant="ghost" onClick={() => navigate(-1)} className="text-[#525C58]" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back
      </Button>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight flex items-center gap-3" style={{ fontFamily: "Manrope, sans-serif" }}>
            <Users className="h-8 w-8 text-[#1B4332]" /> Staff & payroll
          </h1>
          <p className="text-[#525C58] mt-1">House helpers, groundskeepers and other staff with monthly Kenyan payroll (PAYE / NSSF / SHA / AHL).</p>
        </div>
        {canWrite && <Button onClick={openNew} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="new-staff-btn"><Plus className="h-4 w-4 mr-1" /> Add staff</Button>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-[#E8E8E6]" data-testid="stat-headcount">
          <CardContent className="py-4">
            <p className="text-[10px] uppercase tracking-wider text-[#7A8A84] font-semibold">Active staff</p>
            <p className="text-3xl font-bold text-[#1B4332] mt-1 tabular-nums">{staff.filter((s) => s.active).length}</p>
            <p className="text-[10px] text-[#7A8A84] mt-1">{payrollEligible.length} on payroll</p>
          </CardContent>
        </Card>
        <Card className="border-[#E8E8E6]" data-testid="stat-monthly-gross">
          <CardContent className="py-4">
            <p className="text-[10px] uppercase tracking-wider text-[#7A8A84] font-semibold">Monthly gross</p>
            <p className="text-3xl font-bold text-[#B5392F] mt-1 tabular-nums">{fmtKES(totalGross)}</p>
            <p className="text-[10px] text-[#7A8A84] mt-1">across all payroll-eligible staff</p>
          </CardContent>
        </Card>
        <Card className="border-[#E8E8E6]" data-testid="payroll-run-card">
          <CardContent className="py-4 space-y-2">
            <p className="text-[10px] uppercase tracking-wider text-[#7A8A84] font-semibold">Run payroll</p>
            <div className="flex items-center gap-2">
              <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="text-sm" data-testid="period-input" />
              {isAdmin && (
                <Button size="sm" onClick={runPayroll} disabled={running || payrollEligible.length === 0} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="run-payroll-btn">
                  <Play className="h-3.5 w-3.5 mr-1" /> {running ? "Running…" : "Run"}
                </Button>
              )}
            </div>
            <p className="text-[10px] text-[#7A8A84]">Idempotent — re-runs skip already-paid staff for this month.</p>
          </CardContent>
        </Card>
      </div>

      <Card className="border-[#E8E8E6]">
        <CardHeader>
          <CardTitle className="text-base">Roster ({staff.length})</CardTitle>
          <CardDescription className="text-xs">Click "Calc" to preview deductions for a salary.</CardDescription>
        </CardHeader>
        <CardContent>
          <Table data-testid="staff-table">
            <TableHeader><TableRow>
              <TableHead>Name</TableHead><TableHead>Role</TableHead><TableHead>Type</TableHead>
              <TableHead className="text-right">Gross / month</TableHead>
              <TableHead>KRA PIN</TableHead><TableHead>SHA</TableHead><TableHead>NSSF</TableHead>
              <TableHead>Status</TableHead><TableHead />
            </TableRow></TableHeader>
            <TableBody>
              {staff.map((s) => (
                <TableRow key={s.id} data-testid={`staff-row-${s.id}`}>
                  <TableCell>
                    <p className="font-medium">{s.name}</p>
                    {s.phone && <p className="text-[10px] text-[#7A8A84]">{s.phone}</p>}
                  </TableCell>
                  <TableCell className="text-sm">{s.role || "—"}</TableCell>
                  <TableCell><Badge className="bg-slate-100 text-slate-700 border border-slate-200 text-[10px]">{s.employment_type || "—"}</Badge></TableCell>
                  <TableCell className="text-right tabular-nums">{Number(s.base_salary_kes || 0) > 0 ? fmtKES(s.base_salary_kes) : <span className="text-[#A3ACA8]">—</span>}</TableCell>
                  <TableCell className="text-xs">{s.kra_pin || <span className="text-[#A3ACA8]">—</span>}</TableCell>
                  <TableCell className="text-xs">{s.sha_number || <span className="text-[#A3ACA8]">—</span>}</TableCell>
                  <TableCell className="text-xs">{s.nssf_number || <span className="text-[#A3ACA8]">—</span>}</TableCell>
                  <TableCell>
                    {s.is_payroll_eligible
                      ? <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px]">Payroll</Badge>
                      : <Badge className="bg-slate-100 text-slate-700 border border-slate-200 text-[10px]">Casual</Badge>}
                    {!s.active && <Badge className="ml-1 bg-red-50 text-red-700 border border-red-200 text-[9px]">Inactive</Badge>}
                  </TableCell>
                  <TableCell className="text-right space-x-1 whitespace-nowrap">
                    {Number(s.base_salary_kes) > 0 && (
                      <Button size="sm" variant="ghost" onClick={() => previewBreakdown(s.base_salary_kes)} className="text-purple-700" title="Preview deductions" data-testid={`calc-${s.id}`}>
                        <Calculator className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {canWrite && <Button size="sm" variant="ghost" onClick={() => openEdit(s)} className="text-[#1B4332]" data-testid={`edit-${s.id}`}><Pencil className="h-3.5 w-3.5" /></Button>}
                    {isAdmin && <Button size="sm" variant="ghost" onClick={() => del(s)} className="text-red-600" data-testid={`del-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Breakdown popup */}
      <Dialog open={!!breakdown} onOpenChange={(o) => !o && setBreakdown(null)}>
        <DialogContent className="max-w-md" data-testid="breakdown-dialog">
          <DialogHeader><DialogTitle>Payroll breakdown (Kenya)</DialogTitle><DialogDescription>Per-month employee deductions</DialogDescription></DialogHeader>
          {breakdown && (
            <div className="space-y-2 text-sm">
              <Row label="Gross"               v={breakdown.gross_KES} bold />
              <Row label="PAYE"                v={`-${fmtKES(breakdown.paye_KES)}`} />
              <Row label="NSSF (6% capped)"    v={`-${fmtKES(breakdown.nssf_KES)}`} />
              <Row label="SHA (2.75%, min 300)" v={`-${fmtKES(breakdown.sha_KES)}`} />
              <Row label="AHL (1.5%)"          v={`-${fmtKES(breakdown.ahl_KES)}`} />
              <div className="border-t border-[#E8E8E6] pt-2">
                <Row label="Net pay" v={breakdown.net_pay_KES} bold accent />
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={() => setBreakdown(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Staff dialog */}
      <Dialog open={!!draft} onOpenChange={(o) => !o && setDraft(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="staff-dialog">
          <DialogHeader><DialogTitle>{draft?.id ? "Edit staff" : "Add staff"}</DialogTitle></DialogHeader>
          {draft && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Name *</Label><Input value={draft.name} onChange={(e) => setDraft({...draft, name: e.target.value})} data-testid="dlg-name" /></div>
                <div className="space-y-1"><Label>Phone</Label><Input value={draft.phone} onChange={(e) => setDraft({...draft, phone: e.target.value})} data-testid="dlg-phone" /></div>
                <div className="space-y-1"><Label>Role</Label><Input value={draft.role} onChange={(e) => setDraft({...draft, role: e.target.value})} placeholder="House helper / Groundskeeper / Security" data-testid="dlg-role" /></div>
                <div className="space-y-1"><Label>Employment type</Label>
                  <Select value={draft.employment_type} onValueChange={(v) => setDraft({...draft, employment_type: v})}>
                    <SelectTrigger data-testid="dlg-employ"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="permanent">Permanent</SelectItem>
                      <SelectItem value="contract">Contract</SelectItem>
                      <SelectItem value="casual">Casual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label>Start date</Label><Input type="date" value={draft.start_date || ""} onChange={(e) => setDraft({...draft, start_date: e.target.value})} data-testid="dlg-start" /></div>
                <div className="space-y-1"><Label>End date (when leaving)</Label><Input type="date" value={draft.end_date || ""} onChange={(e) => setDraft({...draft, end_date: e.target.value})} data-testid="dlg-end" /></div>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-[#E8E8E6]">
                <div className="space-y-1"><Label>Monthly gross (KES)</Label><Input type="number" min={0} value={draft.base_salary_kes} onChange={(e) => setDraft({...draft, base_salary_kes: e.target.value})} data-testid="dlg-salary" /></div>
                <label className="flex items-center gap-2 pt-6">
                  <input type="checkbox" checked={!!draft.is_payroll_eligible} onChange={(e) => setDraft({...draft, is_payroll_eligible: e.target.checked})} data-testid="dlg-eligible" />
                  <span className="text-sm">Eligible for monthly payroll runs</span>
                </label>
              </div>

              <p className="text-[10px] uppercase tracking-wider text-[#7A8A84] font-semibold pt-2">Kenyan compliance fields</p>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1"><Label className="text-xs">KRA PIN</Label><Input value={draft.kra_pin} onChange={(e) => setDraft({...draft, kra_pin: e.target.value})} placeholder="A012345678B" data-testid="dlg-kra" /></div>
                <div className="space-y-1"><Label className="text-xs">NSSF #</Label><Input value={draft.nssf_number} onChange={(e) => setDraft({...draft, nssf_number: e.target.value})} data-testid="dlg-nssf" /></div>
                <div className="space-y-1"><Label className="text-xs">SHA #</Label><Input value={draft.sha_number} onChange={(e) => setDraft({...draft, sha_number: e.target.value})} placeholder="SHA replaced NHIF Oct 2024" data-testid="dlg-sha" /></div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Bank</Label><Input value={draft.bank_name} onChange={(e) => setDraft({...draft, bank_name: e.target.value})} data-testid="dlg-bank-name" /></div>
                <div className="space-y-1"><Label className="text-xs">Account #</Label><Input value={draft.bank_account} onChange={(e) => setDraft({...draft, bank_account: e.target.value})} data-testid="dlg-bank-acct" /></div>
                <div className="space-y-1"><Label className="text-xs">Next of kin</Label><Input value={draft.next_of_kin} onChange={(e) => setDraft({...draft, next_of_kin: e.target.value})} data-testid="dlg-nok" /></div>
                <div className="space-y-1"><Label className="text-xs">Next of kin phone</Label><Input value={draft.next_of_kin_phone} onChange={(e) => setDraft({...draft, next_of_kin_phone: e.target.value})} data-testid="dlg-nok-phone" /></div>
              </div>

              <div className="space-y-1"><Label>Notes</Label><Textarea rows={2} value={draft.notes} onChange={(e) => setDraft({...draft, notes: e.target.value})} data-testid="dlg-notes" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDraft(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="dlg-save">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, v, bold, accent }) {
  return (
    <div className={`flex items-center justify-between ${accent ? "text-[#1B4332]" : ""}`}>
      <span className={bold ? "font-medium" : "text-[#525C58]"}>{label}</span>
      <span className={`tabular-nums ${bold ? "font-semibold" : ""}`}>{typeof v === "number" ? fmtKES(v) : v}</span>
    </div>
  );
}
